import { deleteIn, setIn } from '@thi.ng/paths';
import type { ReadonlyDeep } from 'type-fest';
import { z } from 'zod';

import { validateEmail } from '$lib/email';
import type { ValidationErrors, ValidationResult } from '$lib/forms';
import { validateResourceName } from '$lib/name-validation';
import { safeParseJSONObject } from '$lib/parse-config';

import {
  type DisableAllowed,
  type NamedResourceBase,
  randomResourceID,
  type ReadonlyTrigger,
  type ReadonlyTriggerEvent,
  ResourceTypes,
  type TriggerEval,
  type TriggerNotification,
} from './config-item';
import type { ReadonlyMachineConfigPart } from './machine-config';

export interface TriggerDraftBase extends NamedResourceBase, DisableAllowed {
  webhooks: TriggerWebhook[];
  emails: TriggerEmail[];
  pushes: TriggerPush[];
  doCommands: TriggerDoCommand[];
  application: string;
}
export interface TriggerDraftLiveness extends TriggerDraftBase {
  triggerType: TriggerTypesMap['PART_ONLINE'] | TriggerTypesMap['PART_OFFLINE'];
}
export interface TriggerDraftDataIngested extends TriggerDraftBase {
  triggerType: TriggerTypesMap['PART_DATA_INGESTED'];
  dataTypes: string[];
}
export interface TriggerDraftConditionalDataIngested extends TriggerDraftBase {
  triggerType: TriggerTypesMap['CONDITIONAL_DATA_INGESTED'];
  dataCapture: TriggerDataCaptureDraft;
  unparsedDataCapture: string;
  evals: TriggerEval[];
}
export interface TriggerDraftConditionalLogsIngested extends TriggerDraftBase {
  triggerType: TriggerTypesMap['CONDITIONAL_LOGS_INGESTED'];
  logLevels: string[];
  logMessageRegex: string;
}
export interface TriggerDraftUnknown extends TriggerDraftBase {
  triggerType: TriggerTypesMap['UNKNOWN'];
  configType: string;
}

export interface TriggerDataCaptureDraft {
  componentId: string;
  method: string;
}

export type TriggerDraft =
  | TriggerDraftLiveness
  | TriggerDraftDataIngested
  | TriggerDraftConditionalDataIngested
  | TriggerDraftConditionalLogsIngested
  | TriggerDraftUnknown;
export type ReadonlyTriggerDraft = ReadonlyDeep<TriggerDraft>;

export interface TriggerWebhookBasicAuth {
  username: string;
  password: string;
}

export interface TriggerWebhook {
  url: string;
  interval: NotificationInterval;
  // undefined = basic auth disabled for this webhook
  basicAuth?: TriggerWebhookBasicAuth;
}
export type ReadonlyTriggerWebhook = ReadonlyDeep<TriggerWebhook>;

export interface TriggerEmail {
  email: string;
  interval: NotificationInterval;
}
export type ReadonlyTriggerEmail = ReadonlyDeep<TriggerEmail>;

export interface TriggerPush {
  recipient: string;
  interval: NotificationInterval;
}
export type ReadonlyTriggerPush = ReadonlyDeep<TriggerPush>;

export interface TriggerDoCommand {
  resource: string;
  // JSON object text, parsed when the draft is saved
  command: string;
  interval: NotificationInterval;
}
export type ReadonlyTriggerDoCommand = ReadonlyDeep<TriggerDoCommand>;

export const isLivenessTriggerDraft = (
  draft: ReadonlyTriggerDraft
): draft is TriggerDraftBase & TriggerDraftLiveness => {
  return isLivenessTriggerType(draft.triggerType);
};

export const isDataIngestedTriggerDraft = (
  draft: ReadonlyTriggerDraft
): draft is TriggerDraftBase & TriggerDraftDataIngested => {
  return isDataIngestedTriggerType(draft.triggerType);
};

export const isConditionalDataIngestedTriggerDraft = (
  draft: ReadonlyTriggerDraft
): draft is TriggerDraftBase & TriggerDraftConditionalDataIngested => {
  return isConditionalDataIngestedTriggerType(draft.triggerType);
};

export const isConditionalLogsIngestedTriggerDraft = (
  draft: ReadonlyTriggerDraft
): draft is TriggerDraftBase & TriggerDraftConditionalLogsIngested => {
  return isConditionalLogsIngestedTriggerType(draft.triggerType);
};

export type TriggerToAdd = Pick<ReadonlyTrigger, 'name' | 'uiFolder'>;

export const ALL_MACHINE_OWNERS_RECIPIENT = 'all_machine_owners';

export const TriggerTypes = {
  PART_ONLINE: 'part_online',
  PART_OFFLINE: 'part_offline',
  PART_DATA_INGESTED: 'part_data_ingested',
  CONDITIONAL_DATA_INGESTED: 'conditional_data_ingested',
  CONDITIONAL_LOGS_INGESTED: 'conditional_logs_ingested',
  UNKNOWN: '',
} as const;

export type TriggerTypesMap = typeof TriggerTypes;
export type TriggerKey = keyof TriggerTypesMap;
export type TriggerType = TriggerTypesMap[TriggerKey];

export const TriggerTypeLabels: Record<TriggerKey, string> = {
  PART_ONLINE: 'Part is online',
  PART_OFFLINE: 'Part is offline',
  PART_DATA_INGESTED: 'Data has been synced to the cloud',
  CONDITIONAL_DATA_INGESTED: 'Conditional data ingestion',
  CONDITIONAL_LOGS_INGESTED: 'Conditional logs ingestion',
  UNKNOWN: '',
} as const;

export const isLivenessTriggerType = (
  type: string
): type is TriggerTypesMap['PART_ONLINE'] | TriggerTypesMap['PART_OFFLINE'] => {
  return livenessTriggerTypes.includes(type);
};

export const isDataIngestedTriggerType = (
  type: string
): type is TriggerTypesMap['PART_DATA_INGESTED'] => {
  return type === TriggerTypes.PART_DATA_INGESTED;
};

export const isConditionalDataIngestedTriggerType = (
  type: string
): type is TriggerTypesMap['CONDITIONAL_DATA_INGESTED'] => {
  return type === TriggerTypes.CONDITIONAL_DATA_INGESTED;
};

export const isConditionalLogsIngestedTriggerType = (
  type: string
): type is TriggerTypesMap['CONDITIONAL_LOGS_INGESTED'] => {
  return type === TriggerTypes.CONDITIONAL_LOGS_INGESTED;
};

export const livenessTriggerTypes = [
  TriggerTypes.PART_ONLINE,
  TriggerTypes.PART_OFFLINE,
] as string[];

export const triggerNotificationTimeTypes = {
  MINUTES: 'Minutes',
  SECONDS: 'Seconds',
  HOURS: 'Hours',
  DAYS: 'Days',
} as const;

export const TriggerNotificationTimeTypesAsArray = Object.values(
  triggerNotificationTimeTypes
);

export const TriggerDataTypes = {
  BINARY: 'binary',
  TABULAR: 'tabular',
  FILE: 'file',
  UNSPECIFIED: 'unspecified',
} as const;

export type TriggerDataType =
  (typeof TriggerDataTypes)[keyof typeof TriggerDataTypes];

export const TriggerDataLabelTypes = {
  BINARY: 'Binary (image)',
  TABULAR: 'Tabular (sensor)',
  FILE: 'File',
  UNSPECIFIED: 'Unspecified',
} as const;

export type TriggerDataLabelType =
  (typeof TriggerDataLabelTypes)[keyof typeof TriggerDataLabelTypes];

export const getTriggerDataTypeFromLabel = (
  label: string
): TriggerDataType | undefined => {
  switch (label) {
    case TriggerDataLabelTypes.BINARY: {
      return TriggerDataTypes.BINARY;
    }
    case TriggerDataLabelTypes.FILE: {
      return TriggerDataTypes.FILE;
    }
    case TriggerDataLabelTypes.TABULAR: {
      return TriggerDataTypes.TABULAR;
    }
    case TriggerDataLabelTypes.UNSPECIFIED: {
      return TriggerDataTypes.UNSPECIFIED;
    }
    default: {
      return undefined;
    }
  }
};

export const getTriggerDataTypeLabelFromType = (
  dataType: string
): TriggerDataLabelType | undefined => {
  switch (dataType) {
    case TriggerDataTypes.BINARY: {
      return TriggerDataLabelTypes.BINARY;
    }
    case TriggerDataTypes.FILE: {
      return TriggerDataLabelTypes.FILE;
    }
    case TriggerDataTypes.TABULAR: {
      return TriggerDataLabelTypes.TABULAR;
    }
    case TriggerDataTypes.UNSPECIFIED: {
      return TriggerDataLabelTypes.UNSPECIFIED;
    }
  }
  return undefined;
};

const dataTypesSchema = z.array(z.coerce.string()).nonempty();

export const getTriggerDataTypeLabelFromTypes = (
  types: readonly string[]
): TriggerDataLabelType[] => {
  return types
    .map((type) => getTriggerDataTypeLabelFromType(type))
    .filter(
      (dataType): dataType is TriggerDataLabelType => dataType !== undefined
    );
};

export const getTriggerDataTypeFromLabels = (
  labels: string[]
): TriggerDataType[] => {
  return labels
    .map((label) => getTriggerDataTypeFromLabel(label))
    .filter((dataType): dataType is TriggerDataType => dataType !== undefined);
};

export const TriggerLogLevels = {
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error',
} as const;

export type TriggerLogLevel =
  (typeof TriggerLogLevels)[keyof typeof TriggerLogLevels];

export const TriggerLogLabelLevels = {
  INFO: 'Info',
  WARN: 'Warn',
  ERROR: 'Error',
} as const;

export type TriggerLogLabelLevel =
  (typeof TriggerLogLabelLevels)[keyof typeof TriggerLogLabelLevels];

export const getTriggerLogLevelFromLabel = (
  label: string
): TriggerLogLevel | undefined => {
  switch (label) {
    case TriggerLogLabelLevels.INFO: {
      return TriggerLogLevels.INFO;
    }
    case TriggerLogLabelLevels.WARN: {
      return TriggerLogLevels.WARN;
    }
    case TriggerLogLabelLevels.ERROR: {
      return TriggerLogLevels.ERROR;
    }
    default: {
      return undefined;
    }
  }
};

export const getTriggerLogLevelLabelFromLevel = (
  level: string
): TriggerLogLabelLevel | undefined => {
  switch (level) {
    case TriggerLogLevels.INFO: {
      return TriggerLogLabelLevels.INFO;
    }
    case TriggerLogLevels.WARN: {
      return TriggerLogLabelLevels.WARN;
    }
    case TriggerLogLevels.ERROR: {
      return TriggerLogLabelLevels.ERROR;
    }
    default: {
      return undefined;
    }
  }
};

export const getTriggerLogLevelLabelFromLevels = (
  levels: readonly string[]
): TriggerLogLabelLevel[] => {
  return levels
    .map((level) => getTriggerLogLevelLabelFromLevel(level))
    .filter(
      (logLevel): logLevel is TriggerLogLabelLevel => logLevel !== undefined
    );
};

export const getTriggerLogLevelFromLabels = (
  labels: string[]
): TriggerLogLevel[] => {
  return labels
    .map((label) => getTriggerLogLevelFromLabel(label))
    .filter((logLevel): logLevel is TriggerLogLevel => logLevel !== undefined);
};

export type TriggerNotificationTimeType =
  (typeof triggerNotificationTimeTypes)[keyof typeof triggerNotificationTimeTypes];

const SECONDS_IN_MINUTE = 60;
const SECONDS_IN_HOUR = 60 * 60;
const SECONDS_IN_DAY = 60 * 60 * 24;

export type TriggerTypeLabel =
  (typeof TriggerTypeLabels)[keyof typeof TriggerTypeLabels];

export const getTriggerLabelFromTriggerType = (triggerType: string): string => {
  switch (triggerType) {
    case TriggerTypes.PART_ONLINE: {
      return TriggerTypeLabels.PART_ONLINE;
    }
    case TriggerTypes.PART_OFFLINE: {
      return TriggerTypeLabels.PART_OFFLINE;
    }
    case TriggerTypes.PART_DATA_INGESTED: {
      return TriggerTypeLabels.PART_DATA_INGESTED;
    }
    case TriggerTypes.CONDITIONAL_DATA_INGESTED: {
      return TriggerTypeLabels.CONDITIONAL_DATA_INGESTED;
    }
    case TriggerTypes.CONDITIONAL_LOGS_INGESTED: {
      return TriggerTypeLabels.CONDITIONAL_LOGS_INGESTED;
    }
    case TriggerTypes.UNKNOWN: {
      return TriggerTypeLabels.UNKNOWN;
    }
  }

  return '';
};

export const getTriggerTypeFromLabel = (triggerLabel: string): string => {
  switch (triggerLabel) {
    case TriggerTypeLabels.PART_ONLINE: {
      return TriggerTypes.PART_ONLINE;
    }
    case TriggerTypeLabels.PART_OFFLINE: {
      return TriggerTypes.PART_OFFLINE;
    }
    case TriggerTypeLabels.PART_DATA_INGESTED: {
      return TriggerTypes.PART_DATA_INGESTED;
    }
    case TriggerTypeLabels.CONDITIONAL_DATA_INGESTED: {
      return TriggerTypes.CONDITIONAL_DATA_INGESTED;
    }
    case TriggerTypeLabels.CONDITIONAL_LOGS_INGESTED: {
      return TriggerTypes.CONDITIONAL_LOGS_INGESTED;
    }
  }

  return '';
};

export const getDataTypesForDataIngestionTrigger = (
  attributes: Record<string, unknown>
): [string, ...string[]] | undefined => {
  const result = dataTypesSchema.safeParse(attributes.data_types);
  return result.success ? result.data : undefined;
};

const isValidTriggerType = (
  triggerType: string
): triggerType is TriggerType => {
  return (
    Object.values(TriggerTypes).includes(triggerType as TriggerType) &&
    triggerType !== TriggerTypes.UNKNOWN
  );
};

const isDivisible = (interval: number, divisor: number) => {
  return !(interval % divisor);
};

/**
 * getBestTimeUnitForSeconds converts the seconds interval on the attribute if it exists
 * into the most human readable time unit to show on the trigger builder card
 */

export interface NotificationInterval {
  timeUnit: number;
  timeType: TriggerNotificationTimeType;
}
export const secondsToInterval = (interval: number): NotificationInterval => {
  // return seconds if < 60
  if (interval < SECONDS_IN_MINUTE) {
    return {
      timeUnit: interval,
      timeType: triggerNotificationTimeTypes.SECONDS,
    };
  }

  // returns minutes if divisible by 60, else seconds
  if (
    interval >= SECONDS_IN_MINUTE &&
    interval < SECONDS_IN_HOUR &&
    isDivisible(interval, SECONDS_IN_MINUTE)
  ) {
    return {
      timeUnit: interval / SECONDS_IN_MINUTE,
      timeType: triggerNotificationTimeTypes.MINUTES,
    };
  }

  // returns hours if divisible by 60 * 60, else minutes, then seconds
  if (interval >= SECONDS_IN_HOUR && interval < SECONDS_IN_DAY) {
    if (isDivisible(interval, SECONDS_IN_HOUR)) {
      return {
        timeUnit: interval / SECONDS_IN_HOUR,
        timeType: triggerNotificationTimeTypes.HOURS,
      };
    }
    if (isDivisible(interval, SECONDS_IN_MINUTE)) {
      return {
        timeUnit: interval / SECONDS_IN_MINUTE,
        timeType: triggerNotificationTimeTypes.MINUTES,
      };
    }
  }

  // returns days if divisible by day, then hours, minutes, then seconds
  if (interval >= SECONDS_IN_DAY) {
    if (isDivisible(interval, SECONDS_IN_DAY)) {
      return {
        timeUnit: interval / SECONDS_IN_DAY,
        timeType: triggerNotificationTimeTypes.DAYS,
      };
    }

    if (isDivisible(interval, SECONDS_IN_HOUR)) {
      return {
        timeUnit: interval / SECONDS_IN_HOUR,
        timeType: triggerNotificationTimeTypes.HOURS,
      };
    }
    if (isDivisible(interval, SECONDS_IN_MINUTE)) {
      return {
        timeUnit: interval / SECONDS_IN_MINUTE,
        timeType: triggerNotificationTimeTypes.MINUTES,
      };
    }
  }

  return {
    timeUnit: interval,
    timeType: triggerNotificationTimeTypes.SECONDS,
  };
};

export const intervalToSeconds = (interval: NotificationInterval): number => {
  switch (interval.timeType) {
    case triggerNotificationTimeTypes.SECONDS: {
      return interval.timeUnit;
    }
    case triggerNotificationTimeTypes.MINUTES: {
      return SECONDS_IN_MINUTE * interval.timeUnit;
    }
    case triggerNotificationTimeTypes.HOURS: {
      return SECONDS_IN_HOUR * interval.timeUnit;
    }
    case triggerNotificationTimeTypes.DAYS: {
      return SECONDS_IN_DAY * interval.timeUnit;
    }
    default: {
      return 0;
    }
  }
};

export const transitionDraftToType = (
  newType: string,
  draft: ReadonlyTriggerDraft
): ReadonlyTriggerDraft => {
  // based on the event type set on the trigger,
  // the type is set on a prior function call when the dropdown is clicked on
  // we populate the default attributes required to make this trigger work
  if (!isValidTriggerType(newType) || newType === draft.triggerType) {
    return draft;
  }

  const {
    id,
    name,
    otherProps,
    webhooks,
    emails,
    pushes,
    doCommands,
    application,
    notes,
    isDisabled,
    uiFolder,
  } = draft;
  const base = {
    id,
    name,
    otherProps,
    webhooks,
    emails,
    pushes,
    doCommands,
    application,
    notes,
    isDisabled,
    uiFolder,
  };

  if (isLivenessTriggerType(newType)) {
    return {
      ...base,
      triggerType: newType,
    };
  }

  if (isDataIngestedTriggerType(newType)) {
    return {
      ...base,
      triggerType: newType,
      dataTypes: [],
    };
  }

  if (isConditionalDataIngestedTriggerType(newType)) {
    return {
      ...base,
      triggerType: newType,
      dataCapture: {
        componentId: '',
        method: '',
      },
      unparsedDataCapture: '',
      evals: [],
    };
  }

  if (isConditionalLogsIngestedTriggerType(newType)) {
    return {
      ...base,
      triggerType: newType,
      logLevels: [TriggerLogLevels.ERROR],
      logMessageRegex: '',
    };
  }

  return {
    ...base,
    triggerType: TriggerTypes.UNKNOWN,
    configType: newType,
    webhooks: [],
    emails: [],
    pushes: [],
    doCommands: [],
    application: '',
  };
};

export const setTrigger = (
  part: ReadonlyMachineConfigPart,
  trigger: ReadonlyTrigger
): ReadonlyMachineConfigPart => {
  return setIn(part, ['triggers', trigger.id], trigger);
};

export const deleteTrigger = (
  part: ReadonlyMachineConfigPart,
  triggerID: string
): ReadonlyMachineConfigPart => {
  return deleteIn(part, ['triggers', triggerID]);
};

export const createTrigger = (trigger: TriggerToAdd): ReadonlyTrigger => ({
  type: ResourceTypes.TRIGGER,
  id: randomResourceID(ResourceTypes.TRIGGER),
  event: {
    type: '',
    configType: '',
  },
  notifications: [],
  name: trigger.name,
  notes: undefined,
  otherProps: {},
  uiFolder: trigger.uiFolder,
});

export const createConditionalDataIngestedTrigger = (
  trigger: TriggerToAdd & { componentId: string }
): ReadonlyTrigger => ({
  ...createTrigger(trigger),
  event: {
    type: TriggerTypes.CONDITIONAL_DATA_INGESTED,
    dataCapture: {
      componentId: trigger.componentId,
      method: '',
    },
    unparsedDataCapture: '',
    evals: [],
  },
});

export const validateTriggerName = (
  { name }: TriggerToAdd,
  resourceNames: ReadonlySet<string>
): ValidationResult<TriggerToAdd> => {
  const values = { name: name.trim().replaceAll(' ', '-') };
  const { errors } = validateResourceName({ name: values.name }, resourceNames);

  return { values, errors };
};

const triggerDraftToTriggerEvent = (
  draft: ReadonlyTriggerDraft
): ReadonlyTriggerEvent => {
  switch (draft.triggerType) {
    case TriggerTypes.PART_ONLINE: {
      return { type: TriggerTypes.PART_ONLINE };
    }
    case TriggerTypes.PART_OFFLINE: {
      return { type: TriggerTypes.PART_OFFLINE };
    }
    case TriggerTypes.PART_DATA_INGESTED: {
      return {
        type: TriggerTypes.PART_DATA_INGESTED,
        dataIngested: {
          dataTypes: draft.dataTypes,
        },
      };
    }
    case TriggerTypes.CONDITIONAL_DATA_INGESTED: {
      return {
        type: TriggerTypes.CONDITIONAL_DATA_INGESTED,
        dataCapture: draft.dataCapture,
        unparsedDataCapture: draft.unparsedDataCapture,
        evals: draft.evals,
      };
    }
    case TriggerTypes.CONDITIONAL_LOGS_INGESTED: {
      return {
        type: TriggerTypes.CONDITIONAL_LOGS_INGESTED,
        logLevels: draft.logLevels,
        logMessageRegex: draft.logMessageRegex || undefined,
      };
    }
    default: {
      return { type: TriggerTypes.UNKNOWN, configType: '' };
    }
  }
};

const triggerDraftToTrigger = (
  triggerDraft: ReadonlyTriggerDraft
): ReadonlyTrigger => {
  const webhookNotifications: TriggerNotification[] = triggerDraft.webhooks.map(
    (webhook) => ({
      type: 'webhook',
      value: webhook.url,
      secondsBetweenNotifications: intervalToSeconds(webhook.interval),
      ...(webhook.basicAuth && {
        auth: { basic: { ...webhook.basicAuth } },
      }),
    })
  );
  const emailNotifications: TriggerNotification[] = triggerDraft.emails.map(
    (email) => ({
      type: 'email',
      value: email.email,
      secondsBetweenNotifications: intervalToSeconds(email.interval),
    })
  );
  const pushNotifications: TriggerNotification[] = triggerDraft.pushes.map(
    (push) => ({
      type: 'push',
      value: push.recipient,
      application: triggerDraft.application,
      secondsBetweenNotifications: intervalToSeconds(push.interval),
    })
  );
  const doCommandNotifications: TriggerNotification[] =
    triggerDraft.doCommands.map((doCommand) => {
      const parsedCommand = safeParseJSONObject(doCommand.command);
      return {
        type: 'do_command',
        value: doCommand.resource.trim(),
        command: parsedCommand.success ? parsedCommand.data : {},
        secondsBetweenNotifications: intervalToSeconds(doCommand.interval),
      };
    });

  return {
    id: triggerDraft.id,
    name: triggerDraft.name,
    type: ResourceTypes.TRIGGER,
    notes: triggerDraft.notes,
    otherProps: triggerDraft.otherProps,
    event: triggerDraftToTriggerEvent(triggerDraft),
    notifications: [
      ...webhookNotifications,
      ...emailNotifications,
      ...pushNotifications,
      ...doCommandNotifications,
    ],
    isDisabled: triggerDraft.isDisabled,
    uiFolder: triggerDraft.uiFolder,
  };
};

const intervalErrors = (
  interval: NotificationInterval,
  triggerType: TriggerDraft['triggerType']
): { interval: ValidationErrors<NotificationInterval> } | undefined => {
  // Conditional logs ingested triggers don't require intervals
  if (isConditionalLogsIngestedTriggerType(triggerType)) {
    return undefined;
  }

  const gteError =
    'the notification value must be an integer greater than or equal to 0';

  if (!Number.isFinite(interval.timeUnit)) {
    return { interval: { timeUnit: gteError } };
  }

  if (interval.timeUnit < 0) {
    return { interval: { timeUnit: gteError } };
  }

  if (!TriggerNotificationTimeTypesAsArray.includes(interval.timeType)) {
    return {
      interval: { timeType: 'Please select a provided a time unit' },
    };
  }

  return undefined;
};

const triggerWebhookBasicAuthErrors = (
  basicAuth: TriggerWebhookBasicAuth | undefined
): { basicAuth: ValidationErrors<TriggerWebhookBasicAuth> } | undefined => {
  if (!basicAuth) {
    return undefined;
  }

  const errors: ValidationErrors<TriggerWebhookBasicAuth> = {
    ...(basicAuth.username.trim() === '' && {
      username: 'a username is required when basic authentication is enabled',
    }),
    ...(basicAuth.password.trim() === '' && {
      password: 'a password is required when basic authentication is enabled',
    }),
  };

  return Object.keys(errors).length > 0 ? { basicAuth: errors } : undefined;
};

export const extractWebhookBasicAuthError = (
  errors?: string | ValidationErrors<TriggerWebhookBasicAuth>
) => {
  if (typeof errors === 'string') {
    return errors;
  }
  return errors?.username ?? errors?.password;
};

export const triggerWebhookErrors = (
  trigger: TriggerWebhook,
  triggerType: TriggerDraft['triggerType']
): ValidationErrors<TriggerWebhook> | undefined => {
  const baseErrors = {
    ...intervalErrors(trigger.interval, triggerType),
    ...triggerWebhookBasicAuthErrors(trigger.basicAuth),
  };

  const url = trigger.url.trim();
  if (url === '') {
    return {
      ...baseErrors,
      url: 'the url for a trigger cannot be empty',
    };
  }

  try {
    // eslint-disable-next-line no-new
    new URL(url);
  } catch {
    return {
      ...baseErrors,
      url: `url: ${trigger.url} is not a valid url`,
    };
  }

  return Object.keys(baseErrors).length > 0 ? baseErrors : undefined;
};

export const triggerEmailErrors = (
  email: TriggerEmail,
  triggerType: TriggerDraft['triggerType']
): ValidationErrors<TriggerEmail> | undefined => {
  const baseErrors = intervalErrors(email.interval, triggerType);

  // Skip validation for the special all_machine_owners recipient.
  if (email.email === ALL_MACHINE_OWNERS_RECIPIENT) {
    return baseErrors;
  }

  if (email.email.trim() === '') {
    return {
      ...baseErrors,
      email: 'the email for a trigger cannot be empty',
    };
  }

  if (!validateEmail(email.email)) {
    return {
      ...baseErrors,
      email: `email: ${email.email} is not a valid email`,
    };
  }

  return baseErrors;
};

export const triggerPushErrors = (
  push: TriggerPush,
  triggerType: TriggerDraft['triggerType']
): ValidationErrors<TriggerPush> | undefined => {
  const baseErrors = intervalErrors(push.interval, triggerType);

  if (push.recipient === ALL_MACHINE_OWNERS_RECIPIENT) {
    return baseErrors;
  }

  if (push.recipient.trim() === '') {
    return {
      ...baseErrors,
      recipient: 'the recipient for a push notification cannot be empty',
    };
  }

  if (!validateEmail(push.recipient)) {
    return {
      ...baseErrors,
      recipient: `recipient: ${push.recipient} is not a valid email`,
    };
  }

  return baseErrors;
};

export const triggerDoCommandErrors = (
  doCommand: ReadonlyTriggerDoCommand,
  triggerType: TriggerDraft['triggerType']
): ValidationErrors<TriggerDoCommand> | undefined => {
  const parsedCommand = safeParseJSONObject(doCommand.command);
  const errors: ValidationErrors<TriggerDoCommand> = {
    ...intervalErrors(doCommand.interval, triggerType),
    ...(doCommand.resource.trim() === '' && {
      resource: 'a resource must be selected for a DoCommand',
    }),
    ...(triggerType === TriggerTypes.PART_OFFLINE && {
      resource: 'a DoCommand cannot run when the part is offline',
    }),
    ...(!parsedCommand.success && { command: parsedCommand.error }),
  };

  return Object.keys(errors).length > 0 ? errors : undefined;
};

export const extractIntervalError = (
  errors?: string | ValidationErrors<NotificationInterval>
) => {
  if (typeof errors === 'string') {
    return errors;
  }
  return errors?.timeUnit ?? errors?.timeType;
};

export const validateTriggerDraft = (
  draft: ReadonlyTriggerDraft
): ValidationResult<ReadonlyTriggerDraft, ReadonlyTrigger> => {
  let validationErrors: ValidationErrors<ReadonlyTriggerDraft> | undefined;
  const sanitizedDraft = {
    ...draft,
  };

  for (const webhook of Object.values(draft.webhooks)) {
    const errors = triggerWebhookErrors(webhook, draft.triggerType);
    if (errors) {
      validationErrors = {
        ...validationErrors,
        webhooks:
          errors.url ??
          extractWebhookBasicAuthError(errors.basicAuth) ??
          extractIntervalError(errors.interval),
      };
    }
  }

  for (const email of Object.values(draft.emails)) {
    const errors = triggerEmailErrors(email, draft.triggerType);
    if (errors) {
      validationErrors = {
        ...validationErrors,
        emails: errors.email ?? extractIntervalError(errors.interval),
      };
    }
  }

  for (const doCommand of draft.doCommands) {
    const errors = triggerDoCommandErrors(doCommand, draft.triggerType);
    if (errors) {
      validationErrors = {
        ...validationErrors,
        doCommands:
          errors.resource ??
          errors.command ??
          extractIntervalError(errors.interval),
      };
    }
  }

  if (!isValidTriggerType(draft.triggerType)) {
    validationErrors = {
      ...validationErrors,
      triggerType: `trigger type must be of type part_online part_offline or part_data_ingested`,
    };
    return {
      values: triggerDraftToTrigger(sanitizedDraft),
      errors: validationErrors,
    };
  }

  if (isDataIngestedTriggerDraft(draft) && draft.dataTypes.length === 0) {
    validationErrors = {
      ...validationErrors,
      dataTypes:
        'There must be at least one data type set for part_data_ingested trigger',
    };
  }

  if (isConditionalDataIngestedTriggerDraft(draft)) {
    if (draft.dataCapture.componentId === '') {
      validationErrors = {
        ...validationErrors,
        dataCapture: {
          componentId: 'A component must be selected',
        },
      };
    } else if (draft.dataCapture.method === '') {
      validationErrors = {
        ...validationErrors,
        dataCapture: {
          method: 'A method must be selected',
        },
      };
    }
  }

  if (isConditionalLogsIngestedTriggerDraft(draft)) {
    if (draft.logLevels.length === 0) {
      validationErrors = {
        ...validationErrors,
        logLevels:
          'There must be at least one log level set for conditional_logs_ingested trigger',
      };
    }

    if (draft.logMessageRegex) {
      try {
        void new RegExp(draft.logMessageRegex, 'u');
      } catch {
        validationErrors = {
          ...validationErrors,
          logMessageRegex: 'Invalid regular expression',
        };
      }
    }
  }

  return {
    values: triggerDraftToTrigger(sanitizedDraft),
    errors: validationErrors,
  };
};

interface TriggerableResourceLike {
  id: string;
  name: string;
  api: { subtype: string };
}

/** Resolve a `componentId` for a conditional data ingested trigger from its
 * raw `unparsedDataCapture` string by looking up a matching resource. This is
 * needed for fragment-imported components, whose IDs are not present in the
 * part's own components map at parse time, so the parser leaves
 * `componentId` blank. */
const resolveDataCaptureComponentId = (
  componentId: string,
  unparsedDataCapture: string,
  triggerableResources: readonly TriggerableResourceLike[] | undefined
): string => {
  if (componentId || !unparsedDataCapture || !triggerableResources) {
    return componentId;
  }
  const [subtype = '', name = ''] = unparsedDataCapture.split(':');
  if (!subtype || !name) {
    return componentId;
  }
  const match = triggerableResources.find(
    (resource) => resource.api.subtype === subtype && resource.name === name
  );
  return match?.id ?? componentId;
};

export const triggerToTriggerDraft = (
  trigger: ReadonlyTrigger,
  triggerableResources?: readonly TriggerableResourceLike[],
  // The trigger has no place to store `application` when there are no push
  // notifications (it lives on each push at the data layer). Pass the previous
  // draft's application here so the user's typed value isn't dropped on every
  // form re-derive while they configure things.
  fallbackApplication = ''
): ReadonlyTriggerDraft => {
  const webhooks = trigger.notifications
    .filter((notification) => notification.type === 'webhook')
    .map((notification) => ({
      url: notification.value,
      interval: secondsToInterval(notification.secondsBetweenNotifications),
      ...(notification.auth?.basic && {
        basicAuth: {
          username: notification.auth.basic.username,
          password: notification.auth.basic.password,
        },
      }),
    }));

  const emails = trigger.notifications
    .filter((notification) => notification.type === 'email')
    .map((notification) => ({
      email: notification.value,
      interval: secondsToInterval(notification.secondsBetweenNotifications),
    }));

  const pushNotifications = trigger.notifications.filter(
    (notification) => notification.type === 'push'
  );
  const pushes = pushNotifications.map((notification) => ({
    recipient: notification.value,
    interval: secondsToInterval(notification.secondsBetweenNotifications),
  }));
  // The application id lives on each push notification at the data layer, but
  // a trigger only has one — so any push notification's application is fine.
  // Fall back to the caller-supplied value so the user's typed application
  // isn't lost when there are no pushes yet to attach it to.
  const application = pushNotifications[0]?.application ?? fallbackApplication;

  const doCommands = trigger.notifications
    .filter((notification) => notification.type === 'do_command')
    .map((notification) => ({
      resource: notification.value,
      command: JSON.stringify(notification.command, null, 2),
      interval: secondsToInterval(notification.secondsBetweenNotifications),
    }));

  const { id, name, otherProps, notes, isDisabled, uiFolder } = trigger;
  const base: TriggerDraftBase = {
    id,
    name,
    otherProps,
    webhooks,
    emails,
    pushes,
    doCommands,
    application,
    notes,
    isDisabled,
    uiFolder,
  };

  switch (trigger.event.type) {
    case TriggerTypes.PART_ONLINE:
    case TriggerTypes.PART_OFFLINE: {
      return {
        ...base,
        triggerType: trigger.event.type,
      };
    }

    case TriggerTypes.PART_DATA_INGESTED: {
      return {
        ...base,
        triggerType: trigger.event.type,
        dataTypes: trigger.event.dataIngested.dataTypes,
      };
    }

    case TriggerTypes.CONDITIONAL_DATA_INGESTED: {
      const { dataCapture, unparsedDataCapture } = trigger.event;
      const resolvedComponentId = resolveDataCaptureComponentId(
        dataCapture.componentId,
        unparsedDataCapture,
        triggerableResources
      );
      return {
        ...base,
        triggerType: trigger.event.type,
        dataCapture:
          resolvedComponentId === dataCapture.componentId
            ? dataCapture
            : { ...dataCapture, componentId: resolvedComponentId },
        unparsedDataCapture,
        evals: trigger.event.evals,
      };
    }

    case TriggerTypes.CONDITIONAL_LOGS_INGESTED: {
      return {
        ...base,
        triggerType: trigger.event.type,
        logLevels: trigger.event.logLevels,
        logMessageRegex: trigger.event.logMessageRegex ?? '',
      };
    }

    default: {
      return {
        ...base,
        triggerType: TriggerTypes.UNKNOWN,
        configType: trigger.event.type,
      };
    }
  }
};
